import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Inbox, KanbanSquare, FileText, ExternalLink, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import InquiriesInbox from "@/components/admin/InquiriesInbox";
import OutreachPipeline from "@/components/admin/OutreachPipeline";
import ContentEditor from "@/components/admin/ContentEditor";
import ChangePasswordModal from "@/components/admin/ChangePasswordModal";

const TABS = [
  { key: "inquiries", label: "Inquiries Inbox", icon: Inbox },
  { key: "pipeline", label: "Outreach Pipeline", icon: KanbanSquare },
  { key: "content", label: "Site Copy", icon: FileText },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [stats, setStats] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);

  const loadStats = () => api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => {
    loadStats();
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const statCards = [
    { label: "FACILITIES TRACKED", value: stats?.facilities_total ?? "—", testid: "stat-facilities" },
    { label: "NEW INQUIRIES", value: stats?.inquiries_new ?? "—", testid: "stat-new-inquiries", accent: true },
    { label: "TOTAL INQUIRIES", value: stats?.inquiries_total ?? "—", testid: "stat-total-inquiries" },
    { label: "CONTRACTED", value: stats?.by_status?.contracted ?? "—", testid: "stat-contracted" },
  ];

  return (
    <div className="min-h-screen bg-[#0D0E11] grain" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0D0E11]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <Logo />
            <span className="hidden sm:inline font-mono-tactical text-[10px] tracking-[0.25em] text-[#6B7280] border-l border-white/10 pl-5">
              OUTREACH COMMAND CENTER
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#9CA3AF] hover:text-amber-500 transition-colors" data-testid="view-site-link">
              View site <ExternalLink size={12} />
            </a>
            <span className="hidden md:inline font-mono-tactical text-[11px] text-[#6B7280]" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={() => setPwOpen(true)} className="inline-flex items-center gap-2 border border-white/15 hover:border-amber-500/50 hover:text-amber-500 text-[#9CA3AF] text-sm px-4 py-2 rounded-full transition-colors" data-testid="change-password-btn">
              <KeyRound size={14} /> Password
            </button>
            <button onClick={doLogout} className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500/50 hover:text-red-400 text-[#9CA3AF] text-sm px-4 py-2 rounded-full transition-colors" data-testid="logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-strip">
          {statCards.map((s) => (
            <div key={s.label} className={`bg-[#151821] border rounded-xl p-5 ${s.accent ? "border-amber-500/40" : "border-white/10"}`} data-testid={s.testid}>
              <div className="font-mono-tactical text-[10px] tracking-[0.2em] text-[#6B7280]">{s.label}</div>
              <div className={`mt-2 font-display font-extrabold text-3xl ${s.accent ? "text-amber-500" : "text-[#F3F4F6]"}`}>{s.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex gap-2 border-b border-white/10 pb-px" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-amber-500 text-amber-500" : "border-transparent text-[#9CA3AF] hover:text-[#F3F4F6]"
              }`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "inquiries" && <InquiriesInbox onChanged={loadStats} />}
          {tab === "pipeline" && <OutreachPipeline onChanged={loadStats} />}
          {tab === "content" && <ContentEditor />}
        </div>
      </main>

      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
    </div>
  );
}
